import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ListingCarCardData } from "@/components/car-listing/listing-car-types";
import {
  saveCompareIds,
  encodeCompareIds,
  COMPARE_URL_PARAM,
} from "@/components/car-listing/compare/compare-share";
import { notify } from "@/lib/notify";

export const MAX_COMPARE_CARS = 2;
export const MIN_COMPARE_CARS = 2;

function syncComparePersistence(ids: number[]) {
  saveCompareIds(ids);
  if (typeof window === "undefined") return;
  if (!window.location.pathname.startsWith("/listings")) return;

  const url = new URL(window.location.href);
  if (ids.length >= MIN_COMPARE_CARS) {
    url.searchParams.set(COMPARE_URL_PARAM, encodeCompareIds(ids));
  } else {
    url.searchParams.delete(COMPARE_URL_PARAM);
  }
  window.history.replaceState(window.history.state, "", url.toString());
}

type CarCompareContextValue = {
  selectedCars: ListingCarCardData[];
  count: number;
  isSelected: (carId: number) => boolean;
  toggleCar: (car: ListingCarCardData) => void;
  removeCar: (carId: number) => void;
  clearAll: () => void;
  restoreCars: (cars: ListingCarCardData[], options?: { open?: boolean }) => void;
  compareOpen: boolean;
  setCompareOpen: (open: boolean) => void;
  openCompare: () => void;
};

const CarCompareContext = createContext<CarCompareContextValue | null>(null);

export function CarCompareProvider({ children }: { children: ReactNode }) {
  const [selectedMap, setSelectedMap] = useState<Map<number, ListingCarCardData>>(() => new Map());
  const [compareOpen, setCompareOpen] = useState(false);

  const selectedCars = useMemo(() => Array.from(selectedMap.values()), [selectedMap]);
  const count = selectedCars.length;

  useEffect(() => {
    syncComparePersistence(selectedCars.map((c) => c.id));
  }, [selectedCars]);

  useEffect(() => {
    if (compareOpen && count < MIN_COMPARE_CARS) {
      setCompareOpen(false);
    }
  }, [compareOpen, count]);

  const isSelected = useCallback((carId: number) => selectedMap.has(carId), [selectedMap]);

  const toggleCar = useCallback((car: ListingCarCardData) => {
    setSelectedMap((prev) => {
      const next = new Map(prev);
      if (next.has(car.id)) {
        next.delete(car.id);
        return next;
      }
      if (next.size >= MAX_COMPARE_CARS) {
        notify.info("يمكنك مقارنة سيارتين فقط. أزل إحداهما أولاً.");
        return prev;
      }
      next.set(car.id, car);
      return next;
    });
  }, []);

  const removeCar = useCallback((carId: number) => {
    setSelectedMap((prev) => {
      if (!prev.has(carId)) return prev;
      const next = new Map(prev);
      next.delete(carId);
      return next;
    });
  }, []);

  const clearAll = useCallback(() => {
    syncComparePersistence([]);
    setSelectedMap(new Map());
    setCompareOpen(false);
  }, []);

  const restoreCars = useCallback((cars: ListingCarCardData[], options?: { open?: boolean }) => {
    const next = new Map<number, ListingCarCardData>();
    for (const car of cars.slice(0, MAX_COMPARE_CARS)) {
      next.set(car.id, car);
    }
    setSelectedMap(next);
    if (options?.open && next.size >= MIN_COMPARE_CARS) {
      setCompareOpen(true);
    }
  }, []);

  const openCompare = useCallback(() => {
    if (count !== MIN_COMPARE_CARS) {
      notify.info("اختر سيارتين للمقارنة");
      return;
    }
    setCompareOpen(true);
  }, [count]);

  const value = useMemo(
    () => ({
      selectedCars,
      count,
      isSelected,
      toggleCar,
      removeCar,
      clearAll,
      restoreCars,
      compareOpen,
      setCompareOpen,
      openCompare,
    }),
    [
      selectedCars,
      count,
      isSelected,
      toggleCar,
      removeCar,
      clearAll,
      restoreCars,
      compareOpen,
      openCompare,
    ],
  );

  return <CarCompareContext.Provider value={value}>{children}</CarCompareContext.Provider>;
}

export function useCarCompare() {
  const ctx = useContext(CarCompareContext);
  if (!ctx) {
    throw new Error("useCarCompare must be used within CarCompareProvider");
  }
  return ctx;
}

export function useCarCompareOptional() {
  return useContext(CarCompareContext);
}
